import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";

import { EmptyState } from "@/components/dashboard/data-table/empty-state";
import { FilterBar } from "@/components/dashboard/data-table/filter-bar";
import { Pagination } from "@/components/dashboard/data-table/pagination";
import {
  TBody,
  TD,
  TEmpty,
  TH,
  THead,
  TR,
  Table,
  TableShell,
} from "@/components/dashboard/data-table/table";
import { PageHeader } from "@/components/dashboard/page-header";
import { QueryState } from "@/components/dashboard/query-state";
import { StatusPill, type StatusTone } from "@/components/dashboard/status-pill";
import { DeviceChangeDecisionPanel } from "@/components/device-changes/decision-panel";
import { PersonAvatar } from "@/components/employees/person-avatar";
import { deviceChanges as copy, nav, status } from "@/content";
import { can } from "@/constants/permissions";
import {
  buildQuery,
  readEnum,
  readPage,
  readPageSize,
  readSearch,
  emptyPage,
} from "@/utils/query-params";
import {
  DEVICE_CHANGE_STATUSES,
  deviceChangeStatusCounts,
  listDeviceChangeRequests,
  type DeviceChangeKind,
  type DeviceChangeRow,
  type DeviceChangeStatus,
} from "@/api/device-changes";
import { formatPhone, presentMaskedPhone } from "@/utils/phone";
import { cn } from "@/utils";
import { ArrowLeft } from "lucide-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useQueryParams } from "@/hooks/shared/useQueryParams";
import { useFmt } from "@/hooks/shared/useFmt";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";
import { useSession } from "@/context/UserContext";

const BASE_PATH = "/dashboard/device-changes";

const STATUS_TONE: Record<DeviceChangeStatus, StatusTone> = {
  pending: "attention",
  approved: "positive",
  rejected: "negative",
};

export const Route = createFileRoute("/_main/dashboard/device-changes/")({
  component: () => (
    <RequirePermission action="deviceChanges.read">
      <DeviceChangesPage />
    </RequirePermission>
  ),
});

function DeviceChangesPage() {
  useDocumentTitle(copy.title);
  const { user } = useSession();
  const params = useQueryParams();
  const fmt = useFmt();

  const activeStatus = readEnum(params, "status", DEVICE_CHANGE_STATUSES);
  const search = readSearch(params);
  const kind: DeviceChangeKind = params.kind === "device" ? "device" : "phone";

  const query = {
    search,
    status: activeStatus,
    kind,
    sort: { column: "createdAt" as const, direction: "desc" as const },
    page: readPage(params),
    pageSize: readPageSize(params),
  };

  const list = useQuery({
    queryKey: ["deviceChanges", "list", query],
    queryFn: () => listDeviceChangeRequests(query),
    placeholderData: keepPreviousData,
  });

  const countsQuery = useQuery({
    queryKey: ["deviceChanges", "counts", kind],
    queryFn: () => deviceChangeStatusCounts(kind),
  });

  const result = list.data ?? emptyPage<DeviceChangeRow>();
  const counts = countsQuery.data ?? {
    pending: 0,
    approved: 0,
    rejected: 0,
    all: 0,
    phone: 0,
    device: 0,
  };
  const canDecide = can(user.role, "deviceChanges.decide");
  const isFiltered = Boolean(search) || activeStatus !== undefined;
  const actionHeader =
    activeStatus === "approved" || activeStatus === "rejected"
      ? copy.columnStatus
      : copy.columnAction;

  const tabs = [
    { value: undefined, label: copy.tabAll, count: counts.all },
    { value: "pending" as const, label: copy.tabPending, count: counts.pending },
    { value: "approved" as const, label: copy.tabApproved, count: counts.approved },
    { value: "rejected" as const, label: copy.tabRejected, count: counts.rejected },
  ];

  const table = (
    <TableShell className="mt-5" busy={list.isPlaceholderData}>
      <Table className="table-fixed">
        <THead>
          <TH className="w-[22%]">{copy.columnRequester}</TH>
          <TH className="w-[20%]">{kind === "phone" ? copy.columnNumber : copy.tabDevice}</TH>
          <TH className="w-[26%]">{copy.columnReason}</TH>
          <TH className="w-[20%]">{actionHeader}</TH>
          <TH className="w-[12%]">{copy.columnFiled}</TH>
        </THead>

        <TBody>
          {result.rows.length === 0 ? (
            <TEmpty colSpan={5}>
              <EmptyState
                variant={isFiltered ? "filtered" : "empty"}
                title={isFiltered ? copy.emptyFilteredTitle : copy.emptyTitle}
                lead={isFiltered ? copy.emptyFilteredLead : copy.emptyLead}
                actionHref={
                  isFiltered
                    ? `${BASE_PATH}${kind === "device" ? buildQuery({}, { kind: "device" }) : ""}`
                    : undefined
                }
                actionLabel={isFiltered ? copy.clearFilters : undefined}
              />
            </TEmpty>
          ) : (
            result.rows.map((row) => (
              <TR key={row.id}>
                <TD>
                  <RequesterCell row={row} />
                </TD>
                <TD>
                  {kind === "phone" ? (
                    <NumberCell phone={row.userPhone} newPhone={row.newPhone} />
                  ) : (
                    <DeviceCell
                      previous={row.previousDeviceId}
                      previousLabel={row.previousDeviceLabel}
                      nextId={row.newDeviceId}
                      nextLabel={row.newDeviceLabel}
                    />
                  )}
                </TD>
                <TD className="whitespace-normal">
                  <ReasonCell reason={row.reason} />
                </TD>
                <TD className="whitespace-normal">
                  {row.status === "pending" && canDecide ? (
                    <DeviceChangeDecisionPanel
                      id={row.id}
                      userName={row.userName}
                      status={row.status}
                      canDecide
                      compact
                    />
                  ) : (
                    <DecidedCell row={row} />
                  )}
                </TD>
                <TD>
                  <p className="tabular whitespace-nowrap text-xs text-n8" dir="ltr">
                    {fmt.dateNumeric(row.createdAt)}
                  </p>
                  <p className="mt-0.5 text-2xs text-n6">{fmt.since(row.createdAt)}</p>
                </TD>
              </TR>
            ))
          )}
        </TBody>
      </Table>

      {result.rows.length > 0 ? (
        <Pagination
          page={result.page}
          pageCount={result.pageCount}
          pageSize={result.pageSize}
          total={result.total}
          params={params}
          basePath={BASE_PATH}
          numerals={user.numeralSystem}
          showPageSize
        />
      ) : null}
    </TableShell>
  );

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader
        eyebrow={nav.groups.people}
        title={copy.title}
        lead={copy.lead}
        eyebrowMark
        className="pb-6"
      />

      {!canDecide ? <p className="mt-5 text-xs text-n6">{copy.readOnlyNote}</p> : null}

      <div className="mt-6 flex flex-wrap gap-1 border-b border-n3">
        {(
          [
            { value: "phone" as const, label: copy.tabPhone },
            { value: "device" as const, label: copy.tabDevice },
          ] as const
        ).map((tab) => {
          const active = kind === tab.value;
          return (
            <Link
              key={tab.value}
              to={`${BASE_PATH}${buildQuery(params, {
                kind: tab.value === "phone" ? null : "device",
                page: null,
              })}`}
              aria-current={active ? "page" : undefined}
              className={cn(
                "-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-xs font-semibold transition-colors duration-200 ease-brand",
                active ? "border-orange text-n9" : "border-transparent text-n6 hover:text-n8",
              )}
            >
              {tab.label}
              <span className="tabular text-[11px] font-semibold text-n6">
                {fmt.count(tab.value === "phone" ? counts.phone : counts.device)}
              </span>
            </Link>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap gap-1 border-b border-n3">
        {tabs.map((tab) => {
          const active = activeStatus === tab.value;
          return (
            <Link
              key={tab.label}
              to={`${BASE_PATH}${buildQuery(params, { status: tab.value ?? null, page: null })}`}
              aria-current={active ? "page" : undefined}
              className={cn(
                "-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-xs font-semibold transition-colors duration-200 ease-brand",
                active ? "border-orange text-n9" : "border-n4 text-n6 hover:text-n8",
              )}
            >
              {tab.label}
              <span className="tabular text-[11px] font-semibold text-n6">{fmt.count(tab.count)}</span>
            </Link>
          );
        })}
      </div>

      <div className="mt-5">
        <FilterBar
          params={params}
          basePath={BASE_PATH}
          searchPlaceholder={copy.searchPlaceholder}
          selects={[]}
          wide
        />
      </div>

      <QueryState
        isPending={list.isPending && !list.data}
        isError={list.isError}
        error={list.error}
        columns={5}
        onRetry={() => {
          void list.refetch();
        }}
      >
        {table}
      </QueryState>
    </div>
  );
}

function DecidedCell({ row }: { row: DeviceChangeRow }) {
  return (
    <div className="flex min-w-0 flex-col items-start gap-1">
      <StatusPill tone={STATUS_TONE[row.status]}>{status[row.status]}</StatusPill>
      {row.deciderName ? (
        <p className="text-xs text-n6">
          {copy.decidedBy} {row.deciderName}
        </p>
      ) : null}
      {row.status === "rejected" && row.rejectReason ? (
        <p className="break-words text-xs leading-relaxed text-destructive">{row.rejectReason}</p>
      ) : null}
    </div>
  );
}

function ReasonCell({ reason }: { reason: string | null }) {
  const text = reason?.trim() ?? "";
  if (!text) return <span className="text-n6">{copy.noReason}</span>;
  const lines = text.split(/\n+/);
  const title = lines.length > 1 ? lines[0] : "";
  const body = lines.length > 1 ? lines.slice(1).join(" ") : text;
  return (
    <div className="min-w-0">
      {title ? <p className="break-words text-sm font-semibold text-n8">{title}</p> : null}
      <p
        className={
          title
            ? "mt-0.5 break-words text-xs leading-relaxed text-n6"
            : "break-words text-sm leading-relaxed text-n8"
        }
      >
        {body}
      </p>
    </div>
  );
}

function DeviceCell({
  previous,
  previousLabel,
  nextId,
  nextLabel,
}: {
  previous: string | null;
  previousLabel?: string | null;
  nextId: string;
  nextLabel?: string | null;
}) {
  const current = previousLabel?.trim() || previous;
  const next = nextLabel?.trim() || nextId;
  return (
    <div className="min-w-0 space-y-1 text-xs text-n6">
      <p className="break-words">
        <span>{copy.currentNumber}: </span>
        <span className="text-n8" dir="ltr">
          {current ?? copy.noPreviousDevice}
        </span>
      </p>
      <p className="break-words">
        <span>{copy.newNumber}: </span>
        <span className="font-semibold text-n9" dir="ltr">
          {next}
        </span>
      </p>
    </div>
  );
}

function NumberCell({ phone, newPhone }: { phone: string | null; newPhone?: string | null }) {
  const masked = presentMaskedPhone(phone);
  const next = formatPhone(newPhone);
  if (!masked && !next) return <span className="text-n6">{copy.noPhone}</span>;
  return (
    <div className="flex min-w-0 flex-col items-start gap-2">
      <p className="flex flex-wrap items-center gap-2 text-xs text-n6">
        {masked ? (
          <>
            <span>{copy.currentNumber}</span>
            <span className="tabular" dir="ltr">
              {masked}
            </span>
          </>
        ) : null}
        {next ? (
          <>
            <ArrowLeft aria-hidden className="size-3 shrink-0" strokeWidth={1.6} />
            <span>{copy.newNumber}</span>
          </>
        ) : null}
      </p>
      {next ? (
        <p className="tabular text-xs font-semibold text-n9" dir="ltr">
          {next}
        </p>
      ) : null}
    </div>
  );
}

function RequesterCell({ row }: { row: DeviceChangeRow }) {
  return (
    <div className="flex items-center gap-3">
      <PersonAvatar name={row.userName} photoUrl={row.photoUrl} size="xs" />
      <div className="min-w-0">
        <Link
          to={`${BASE_PATH}/${row.id}`}
          className="text-sm font-semibold text-n9 hover:text-brand"
        >
          {row.userName}
        </Link>
        {row.nationalIdLast4 ? (
          <p className="mt-0.5 flex items-center gap-1 text-xs text-n6">
            <span>{copy.idSuffix}</span>
            <span className="tabular" dir="ltr">
              {row.nationalIdLast4}
            </span>
          </p>
        ) : null}
        {row.userRole === "supervisor" ? (
          <p className="mt-0.5 text-2xs font-semibold text-orange-700">{copy.supervisorNote}</p>
        ) : null}
      </div>
    </div>
  );
}
